import type * as React from 'react';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { onIdTokenChanged } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { auth } from '@/lib/firebase';
import { verifyAdminLoginCallable } from '@/lib/callables';
import { APP_VERSION } from '@/lib/appVersion';
import { trackEvents } from '@/lib/events';
import { identifyAdmin, resetPostHog } from '@/lib/posthog';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import {
  EMPTY_TOKEN_SESSION_MARKS,
  evaluateAdmin,
  isNewAuthorizedSession,
  shouldForceRefresh,
  type AdminAuthStatus,
  type IdTokenClaims,
  type TokenSessionMarks,
} from './authStatus';
import { clearSignInIntent, consumeSignInIntent, signOut } from './firebaseAuth';

/** Valor do contexto: estado de identidade do admin + `signOut` do Firebase Auth. */
type AuthContextValue = {
  status: AdminAuthStatus;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const TAG = 'admin/auth';

/**
 * Fonte única do estado de login do admin: ouve o Firebase Auth e decide se
 * quem está logado é super-admin.
 *
 * Origem: sem issue — commit 88be39195, 2026-04-26 (Sprint 2, Google SSO
 * restrito + guard de superAdmin); a revalidação pela callable entrou em
 * eebbfc524, 2026-04-26.
 *
 * Por quê: o admin não tem tela própria de cadastro — o acesso é a soma de
 * conta do Workspace `@etiquetachef.com`, e-mail verificado e claim
 * `superAdmin`. Centralizar a avaliação aqui faz o guard
 * (`RequireSuperAdmin`) e a tela de acesso restrito lerem o mesmo veredito.
 * `onIdTokenChanged` (e não `onAuthStateChanged`) para reavaliar quando o
 * token é renovado — é aí que um claim concedido ou revogado aparece.
 *
 * Contrato: expõe `{ status, signOut }` por contexto. `status` começa
 * `LOADING` e vai para `SIGNED_OUT`, `AUTHORIZED` ou uma variante de recusa
 * (`evaluateAdmin`). Efeitos: `getIdTokenResult` (rede; forçado só na 1ª
 * emissão de cada uid), PostHog (`identifyAdmin`/`resetPostHog`), eventos
 * `login` (só no AUTHORIZED que veio de sign-in nesta página, #1037) e
 * `logout` (só de quem tinha entrado), e a callable
 * `verifyAdminLogin` a cada token de quem está autorizado (~1 vez por hora).
 *
 * Usado por: `main.tsx` (envolve o app inteiro); lido via `useAuth` em
 * `auth/RequireSuperAdmin.tsx`, `routes/Login.tsx`, `routes/AdminLayout.tsx`,
 * `routes/RestrictedAccess.tsx`, `routes/TenantsPage.tsx`,
 * `routes/SuperAdminsPage.tsx`, `features/settings/NotificationPrefsCard.tsx`,
 * `features/dashboard-alerts/AcknowledgeButton.tsx`.
 *
 * Arquitetura: o cliente só decide o que MOSTRAR. Quem autoriza de verdade é
 * o claim `superAdmin` nas `firestore.rules` e nas callables
 * (`apps/functions/src/auth/permissions.ts`); a callable
 * `verifyAdminLogin` (`apps/functions/src/admin/verifyAdminLogin.ts`) é a
 * camada extra que derruba a sessão se o servidor recusar.
 *
 * Falhas conhecidas:
 *  - Falha ao ler o token (rede) cai em `SIGNED_OUT` e só vai para o
 *    `console.error`, sem report na telemetria (#926, achado da frente de
 *    docstrings).
 *  - Até 2026-10-13 (#963): `getIdTokenResult(true)` em toda emissão
 *    realimentava o listener — loop com 78.349 `login` em 7 dias de uma
 *    pessoa e `verifyAdminLogin` em rajada. Marcas em `TokenSessionMarks`.
 *
 * Dicas: só `functions/permission-denied` da `verifyAdminLogin` desloga;
 * erro de rede ou cold start mantém a sessão de propósito — trocar isso
 * derruba o admin a cada deploy de Functions.
 *
 * Issues: #926 (docstrings da frente Admin, épico #796), #963 (loop do
 * refresh forçado), #939 (achado na telemetria), #1037 (`login` em recarga).
 *
 * Histórico:
 *  - 2026-10-13 17:45 +0200 — (#963) refresh forçado só na 1ª emissão do
 *    uid; `login`/`logout` só na transição.
 *  - 2026-10-26 21:15 +0100 — (#1037) `login` só depois de sign-in por popup
 *    nesta página (`consumeSignInIntent`); recarga não conta.
 */
export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [status, setStatus] = useState<AdminAuthStatus>({ kind: 'LOADING' });
  // Marcas entre emissões do listener (#963): ref, não state — mudar a marca
  // não pode re-renderizar nem reinscrever o listener.
  const marks = useRef<TokenSessionMarks>(EMPTY_TOKEN_SESSION_MARKS);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, (user) => {
      void (async (): Promise<void> => {
        if (!user) {
          console.info(`${TAG}: signed out`);
          // `logout` só para quem tinha entrado: a emissão nula do boot (sem
          // sessão) não é uma saída.
          if (marks.current.authorizedUid !== null) trackEvents.logout();
          marks.current = EMPTY_TOKEN_SESSION_MARKS;
          clearSignInIntent();
          resetPostHog();
          setStatus({ kind: 'SIGNED_OUT' });
          return;
        }
        try {
          // Bug corrigido em 2026-10-13 17:45 +0200 (#963): forçar o refresh
          // em TODA emissão realimentava este listener (o token novo dispara
          // `onIdTokenChanged` de novo) — 78 mil `login` numa semana e a
          // `verifyAdminLogin` em rajada. Força só a primeira emissão de cada
          // uid (é o que propaga um claim recém-emitido pelo `setSuperAdmin`)
          // e marca ANTES do await, porque a emissão reentrante chega antes
          // dele voltar. Não volte para `getIdTokenResult(true)` incondicional.
          const force = shouldForceRefresh(marks.current, user.uid);
          marks.current = { ...marks.current, forcedUid: user.uid };
          const tokenResult = await user.getIdTokenResult(force);
          const claims = tokenResult.claims as IdTokenClaims;
          const next = evaluateAdmin(user, claims);
          console.info(`${TAG}: status=${next.kind} email=${user.email ?? '<no-email>'}`);
          setStatus(next);

          if (next.kind === 'AUTHORIZED') {
            // `login`/`identify` só na entrada; renovação horária do token de
            // quem já está dentro não é login novo (#963).
            if (isNewAuthorizedSession(marks.current, next.user.uid)) {
              marks.current = { ...marks.current, authorizedUid: next.user.uid };
              identifyAdmin({
                userId: next.user.uid,
                email: next.user.email ?? '',
                appVersion: APP_VERSION,
              });
              // Bug corrigido em 2026-10-26 21:15 +0100 (#1037): `marks` nasce
              // vazio a cada carregamento, então recarga e aba nova com sessão
              // restaurada emitiam `login`. Agora só emite quem passou pelo
              // popup nesta página. `identify` continua a cada sessão de página.
              // Mesma classe do `login` do `apps/app` (#1036).
              if (consumeSignInIntent()) trackEvents.login();
            }
          } else {
            marks.current = { ...marks.current, authorizedUid: null };
            resetPostHog();
          }

          // Camada extra: revalida server-side via callable. Função
          // revoga o claim em runtime se o admin foi removido entre o
          // token refresh e agora. Só desconectamos em permission-denied
          // — outros erros (network, função fria) não derrubam a sessão.
          if (next.kind === 'AUTHORIZED') {
            try {
              await verifyAdminLoginCallable();
            } catch (verifyErr) {
              if (
                verifyErr instanceof FirebaseError &&
                verifyErr.code === 'functions/permission-denied'
              ) {
                console.warn(`${TAG}: server revogou acesso, signing out`);
                await signOut();
              } else {
                console.warn(
                  `${TAG}: verifyAdminLogin falhou (mantendo sessão)`,
                  verifyErr,
                );
              }
            }
          }
        } catch (err) {
          console.error(`${TAG}: failed to read id token`, err);
          // #1021 (2026-10-23): a falha do refresh derruba a sessão (`SIGNED_OUT`) e
          // só ia para o console — o super-admin caía no login sem rastro algum em
          // produção. `code` separa rede (`auth/network-request-failed`) de token
          // revogado. A falha do `verifyAdminLogin` logo acima já é reportada pelo
          // wrapper das callables (`callable_failed`).
          reportNonFatal('admin_id_token_failed', {
            code: (err as { code?: string }).code ?? null,
            ...serializeError(err),
          });
          // Refresh falhou (rede): a próxima emissão tenta forçar de novo —
          // falha não emite token, então não há loop (#963).
          marks.current = EMPTY_TOKEN_SESSION_MARKS;
          setStatus({ kind: 'SIGNED_OUT' });
        }
      })();
    });
    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider value={{ status, signOut }}>{children}</AuthContext.Provider>
  );
}

/** Lê o contexto do `AuthProvider`; lança se usado fora dele (erro de montagem, não de runtime). */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth: chamado fora de <AuthProvider>');
  return ctx;
}
